import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import ConfirmationDialog from 'live/components/actions/ui/confirmation-dialog'
import styled from 'styled-components'
import { postAnalysis } from '../../common/apis'
import { handleResponse } from '../../common/utils'
import { SensorialTabContext } from './sensorialTab'
import moment from 'moment-timezone'
import { ParamsContext } from '../qualityMonitoring'

interface IProps {
    showModal: boolean
    setShowModal: Dispatch<SetStateAction<unknown>>
    selectedStoppedLine?: StoppedLineEvent
}
export function StoppedLineModal({ showModal, setShowModal, selectedStoppedLine }: IProps): JSX.Element {
    const urlParams = useContext(ParamsContext)

    const [endDate, setEndDate] = useState(
        selectedStoppedLine?.endDate ? moment(selectedStoppedLine?.endDate).format('YYYY-MM-DD') : ''
    )
    const [endTime, setEndTime] = useState(
        selectedStoppedLine?.endDate ? moment(selectedStoppedLine?.endDate).format('HH:mm') : ''
    )
    const [startDate, setStartDate] = useState(
        selectedStoppedLine?.startDate ? moment(selectedStoppedLine?.startDate).format('YYYY-MM-DD') : ''
    )
    const [startTime, setStartTime] = useState(
        selectedStoppedLine?.startDate ? moment(selectedStoppedLine?.startDate).format('HH:mm') : ''
    )
    const [reason, setReason] = useState(selectedStoppedLine?.reason ? selectedStoppedLine?.reason : '')
    const [showErrors, setShowErrors] = useState(false)
    const [errorInInputs, setErrorInInputs] = useState(true)
    const setUpdateList = useContext(SensorialTabContext)
    function handleHide(): void {
        setShowModal(false)
        setEndDate('')
        setEndTime('')
        setReason('')
    }
    useEffect(() => {
        const isError = !endDate || !endTime || !startDate || !startTime || !reason
        setErrorInInputs(isError)
        if (!isError && showErrors) setShowErrors(false)
    }, [endDate, endTime, startDate, startTime, reason, showErrors])
    useEffect(() => {
        if (selectedStoppedLine) {
            setEndDate(moment(selectedStoppedLine?.endDate).format('YYYY-MM-DD'))
            setEndTime(moment(selectedStoppedLine?.endDate).format('HH:mm'))
            setStartDate(moment(selectedStoppedLine?.startDate).format('YYYY-MM-DD'))
            setStartTime(moment(selectedStoppedLine?.startDate).format('HH:mm'))
            setReason(selectedStoppedLine?.reason)
        } else {
            setStartDate('')
            setStartTime('')
            setEndDate('')
            setEndTime('')
            setReason('')
        }
    }, [selectedStoppedLine])

    function handleApply(): Promise<unknown> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [paramsTab, paramsCountry, paramsFactory, paramsLine] = urlParams.split('/')
        if (selectedStoppedLine) {
            setShowModal(false)
        } else if (errorInInputs) {
            setShowErrors(true)

            return new Promise(() => {
                throw new Error("Stop wasn't registered due to missing data")
            })
        } else {
            const endDateTime = new Date(endDate + ' ' + endTime).getTime()
            const startDateTime = new Date(startDate + ' ' + startTime).getTime()
            postAnalysis({
                endDate: endDateTime,
                startDate: startDateTime,
                reason,
                analysisType: 'stopped line',
                country: paramsCountry,
                factory: paramsFactory,
                line: paramsLine
            }).then(postResponse => {
                handleResponse(postResponse, 'stopped line')
                setUpdateList(true)
            })
        }
    }

    return (
        <ConfirmationDialog
            show={showModal}
            onHide={handleHide}
            title={i18n('Stopped Line')}
            btnStyle={errorInInputs ? 'notAllowed' : 'primary'}
            cancelText={i18n('cancel')}
            className={'stoppedLine-confirmation-modal modal-sm'}
            content={
                <DialogContent
                    endDate={endDate}
                    setEndDate={setEndDate}
                    endTime={endTime}
                    setEndTime={setEndTime}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    startTime={startTime}
                    setStartTime={setStartTime}
                    reason={reason}
                    setReason={setReason}
                    showErrors={showErrors}
                    disabled={!!selectedStoppedLine}
                />
            }
            btnText={i18n(selectedStoppedLine ? 'close' : 'Register Stopped Line')}
            apply={handleApply}
            disableCancel={!!selectedStoppedLine}
        />
    )
}
interface IContentProps {
    endDate: string
    setEndDate: (string) => void
    endTime: string
    setEndTime: (string) => void
    startDate: string
    setStartDate: (string) => void
    startTime: string
    setStartTime: (string) => void
    reason: string
    showErrors: boolean
    setReason: (string) => void
    disabled: boolean
}

function DialogContent({
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    reason,
    setReason,
    showErrors,
    disabled
}: IContentProps): JSX.Element {
    return (
        <StyledDialogContent className={'dialogContent'}>
            <div className="dialogContent--start">
                <h3 className={'dialogContent--start__title'}>{i18n('start')}</h3>
                <div className="dialogContent--start__dateTime">
                    <label>
                        {i18n('Date')}
                        <input
                            onChange={event => setStartDate(event.target.value)}
                            value={startDate}
                            type="date"
                            className={`dialogContent--start__dateTime--date ${
                                !startDate && showErrors ? 'hasError' : ''
                            }`}
                            name="dialogContent--start__dateTime--date"
                            required
                            disabled={disabled}
                        />
                    </label>
                    <label>
                        {i18n('Horário')}
                        <input
                            onChange={event => setStartTime(event.target.value)}
                            value={startTime}
                            type="time"
                            className={`dialogContent--start__dateTime--time ${
                                !startTime && showErrors ? 'hasError' : ''
                            }`}
                            name="dialogContent--start__dateTime--time"
                            required
                            disabled={disabled}
                        />
                    </label>
                </div>
            </div>
            <div className="dialogContent--end">
                <h3 className={'dialogContent--end__title'}>{i18n('end')}</h3>
                <div className="dialogContent--end__dateTime">
                    <label>
                        {i18n('Date')}
                        <input
                            onChange={event => setEndDate(event.target.value)}
                            value={endDate}
                            type="date"
                            className={`dialogContent--end__dateTime--date ${!endDate && showErrors ? 'hasError' : ''}`}
                            name="dialogContent--end__dateTime--date"
                            required
                            disabled={disabled}
                        />
                    </label>
                    <label>
                        {i18n('Horário')}
                        <input
                            onChange={event => setEndTime(event.target.value)}
                            value={endTime}
                            type="time"
                            className={`dialogContent--end__dateTime--time ${!endTime && showErrors ? 'hasError' : ''}`}
                            name="dialogContent--end__dateTime--time"
                            required
                            disabled={disabled}
                        />
                    </label>
                </div>
            </div>
            <label htmlFor="dialogContent--reason">{i18n('Reason')}</label>
            <textarea
                onChange={event => setReason(event.target.value)}
                value={reason}
                id="dialogContent--reason"
                className={`dialogContent--reason ${!reason && showErrors ? 'hasError' : ''}`}
                name="dialogContent--reason"
                placeholder={i18n('Write here the why the line has stopped')}
                disabled={disabled}
            ></textarea>
        </StyledDialogContent>
    )
}

const StyledDialogContent = styled.div`
    .dialogContent {
        &--reason,
        &--start__dateTime--date,
        &--end__dateTime--date,
        &--end__dateTime--time,
        &--start__dateTime--time {
            border: 1px solid ${({ theme }) => theme['gray-300']};
            &.hasError {
                border: 1px solid ${({ theme }) => theme['red-500']};
            }
            &:disabled {
                background-color: var(--gray-200);
                color: var(--gray-800);
            }
        }
        &--start,
        &--end {
            display: flex;
            flex-direction: column;
            &__title {
                font-family: var(--font-semibold);
                text-transform: uppercase;
                margin-bottom: 7px;
            }
            &__dateTime {
                label {
                    margin-right: 15px;
                }
                display: flex;
                &--time,
                &--date {
                    height: 30px;
                    margin-bottom: 20px;
                    border-radius: 4px;
                    &:invalid {
                        color: var(--gray-400);
                    }
                }
            }
        }
        &--reason {
            width: 100%;
            height: 100px;
        }
    }
`
